import dataBrowserApi from '../../api/day6DataBrowser'
import NetworkCommand from '../NetworkCommand'
import { firestoreDoc, getDoc } from '../../util/firestoreUtil'
import Vue from 'vue'

const snapshotDoc = path => getDoc(firestoreDoc(path))

const MUTA = {
  isSettingModifiedId: 'isSettingModified',
  savingSummaryName: 'savingSummaryName',
  savingSummaryNotes: 'savingSummaryNotes',
  sharingCharacter: 'sharingCharacter',
  deletingCharacter: 'deletingCharacter',
  isUpdatingPoseSequenceName: 'isUpdatingPoseSequenceName',
  isSettingActivePoseSequence: 'isSettingActivePoseSequence',
  isDeletingPoseSequence: 'isDeletingPoseSequence'
}

// initial state
const initialState = {
  isSettingModifiedId: '',
  savingSummaryName: {},
  savingSummaryNotes: {},
  sharingCharacter: {},
  deletingCharacter: {},
  isUpdatingPoseSequenceName: false,
  isSettingActivePoseSequence: false,
  isDeletingPoseSequence: false
}

// getters
const moduleGetters = {
  isSavingName: (state) => (id) => state.savingSummaryName[id],
  isSavingNotes: (state) => (id) => state.savingSummaryNotes[id],
  // TODO Change/disable day6.shareAccountCharacter once this is functional
  isSharingCharacter: (state) => (id) => state.sharingCharacter[id],
  isDeletingCharacter: (state) => (id) => state.deletingCharacter[id]
}

// actions
const actions = {
  async saveCharacterName ({ getters, commit, rootGetters }, { id, name }) {
    if (getters.isSavingName(id) ||
        !rootGetters.isAccountPaid) { return }

    return new NetworkCommand(
      commit,
      b => commit(MUTA.savingSummaryName, { docId: id, isSaving: b }),
      async () => dataBrowserApi.updateSummaryName(id, name)
    ).executeAsync()
  },
  async saveCharacterNotes ({ getters, commit, rootGetters }, { id, notes }) {
    if (getters.isSavingNotes(id) ||
        !rootGetters.isAccountPaid) { return }

    return new NetworkCommand(
      commit,
      b => commit(MUTA.savingSummaryNotes, { docId: id, isSaving: b }),
      async () => dataBrowserApi.updateSummaryNotes(id, notes)
    ).executeAsync()
  },
  async orderCharacterFirst ({ state, commit, rootGetters }, idSummary) {
    if (state.isSettingModifiedId ||
        !rootGetters.isAccountPaid) { return }

    return new NetworkCommand(
      commit,
      b => commit(MUTA.isSettingModifiedId, b ? idSummary : null),
      async () => dataBrowserApi.updateSummaryModified(idSummary)
    ).executeAsync()
  },
  async saveCharacterSharing ({ getters, commit, rootGetters }, { idSummary, shareLevel }) {
    if (getters.isSharingCharacter(idSummary) ||
        !rootGetters.isAccountPaid) { return }

    return new NetworkCommand(
      commit,
      b => commit(MUTA.sharingCharacter, { docId: idSummary, isSharing: b }),
      async () => dataBrowserApi.updateShareLevel({ idSummary, shareLevel })
    ).executeAsync()
  },
  async deleteCharacter ({ getters, commit, rootGetters }, idSummary) {
    if (getters.isDeletingCharacter(idSummary) ||
        !rootGetters.isAccountPaid) { return }

    return new NetworkCommand(
      commit,
      b => commit(MUTA.deletingCharacter, { docId: idSummary, isDeleting: b }),
      async () => dataBrowserApi.deleteSummary(idSummary)
    ).executeAsync()
  },
  async queryPoseSequence ({ rootGetters }, idSequence) {
    if (!rootGetters.isAccountPaid) { return {} }

    const accountId = rootGetters.accountId()
    const poseSequenceSnap = await snapshotDoc(`day6-data/${accountId}/day6-account-pose-sequence/${idSequence}`)
    return poseSequenceSnap.exists() && !poseSequenceSnap.get('meta.dlt')
      ? {
          id: idSequence,
          data: poseSequenceSnap.get('sequenceData')
        }
      : {}
  },
  async queryCharacterAnimations ({ rootGetters, dispatch }, dimensionsId) {
    if (!rootGetters.isAccountPaid) { return {} }

    const accountId = rootGetters.accountId()
    const dimensionsSnap = await snapshotDoc(`day6-data/${accountId}/body-dimensions/${dimensionsId}`)
    const idAnimations = dimensionsSnap.exists() ? dimensionsSnap.get('dimensions.idAnimations') : ''
    let sequences = {}
    let activeSequence = ''
    if (idAnimations) {
      const animationGroupData = await dispatch('queryAnimationGroup', idAnimations)
      activeSequence = animationGroupData.activeSequence
      sequences = animationGroupData.sequenceMap
    }
    return { activeSequence, sequences }
  },
  async queryAnimationGroup ({ rootGetters }, idAnimationGroup) {
    if (!rootGetters.isAccountPaid) { return {} }

    const accountId = rootGetters.accountId()
    const summarySnap = await snapshotDoc(`day6-data/${accountId}/day6-account-animation-group/${idAnimationGroup}`)
    return summarySnap.exists() ? summarySnap.data().exin : {}
  },
  async savePoseSequenceName ({ commit, rootGetters, dispatch }, { idAnimationGroup, idSequence, name }) {
    if (!rootGetters.isAccountPaid) { return {} }

    return new NetworkCommand(
      commit,
      MUTA.isSettingActivePoseSequence,
      async () => {
        const isUpdated = await dataBrowserApi.updatePoseSequenceName({ idAnimationGroup, idSequence, name })
        if (isUpdated) {
          const animationData = await dispatch('queryAnimationGroup', idAnimationGroup)
          return animationData.sequenceMap || {}
        }
        return {}
      }
    ).executeAsync()
  },
  async saveActivePoseSequence ({ commit, rootGetters }, { idAnimationGroup, idSequence }) {
    if (!rootGetters.isAccountPaid) { return {} }

    return new NetworkCommand(
      commit,
      MUTA.isSettingActivePoseSequence,
      async () => dataBrowserApi.setActivePoseSequence({ idAnimationGroup, idSequence })
    ).executeAsync()
  },
  async deletePoseSequence ({ commit, rootGetters }, { idAnimationGroup, idSequence }) {
    if (!rootGetters.isAccountPaid) { return {} }

    return new NetworkCommand(
      commit,
      MUTA.isDeletingPoseSequence,
      async () => dataBrowserApi.deletePoseSequence({ idAnimationGroup, idSequence })
    ).executeAsync()
  }
}

const mutateSet = (data, docId, isAdding) => {
  Vue.set(data, docId, isAdding)
}

// mutations
const mutations = {
  [MUTA.isSettingModifiedId] (s, id) {
    s.isSettingModifiedId = id
  },
  [MUTA.savingSummaryName] (s, { docId, isSaving }) {
    mutateSet(s.savingSummaryName, docId, isSaving)
  },
  [MUTA.savingSummaryNotes] (s, { docId, isSaving }) {
    mutateSet(s.savingSummaryNotes, docId, isSaving)
  },
  [MUTA.sharingCharacter] (s, { docId, isSharing }) {
    mutateSet(s.sharingCharacter, docId, isSharing)
  },
  [MUTA.deletingCharacter] (s, { docId, isDeleting }) {
    mutateSet(s.deletingCharacter, docId, isDeleting)
  },
  [MUTA.isUpdatingPoseSequenceName] (s, b) {
    s.isUpdatingPoseSequenceName = b
  },
  [MUTA.isSettingActivePoseSequence] (s, b) {
    s.isSettingActivePoseSequence = b
  },
  [MUTA.isDeletingPoseSequence] (s, b) {
    s.isDeletingPoseSequence = b
  }
}

export default {
  namespaced: true,
  state: initialState,
  getters: moduleGetters,
  actions,
  mutations
}
